/**
 * Items tab (BON-161) — searchable, filterable catalog item list with
 * create / edit / merge. Admin-gated actions are hidden for non-admins.
 */
import { Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import {
  CATALOG_CATEGORIES,
  fetchCatalogItems,
  type CatalogItem,
} from '../../../api/inventory-catalog';

import ItemFormModal from './ItemFormModal';
import MergeItemsModal from './MergeItemsModal';

const summarize = (attrs: Record<string, unknown>): string =>
  Object.entries(attrs)
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(' · ');

const selectClass =
  'rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] px-2 py-1.5 text-sm';

interface FilterBarProps {
  search: string;
  category: string;
  includeMerged: boolean;
  count: number;
  canEdit: boolean;
  onSearch: (v: string) => void;
  onCategory: (v: string) => void;
  onIncludeMerged: (v: boolean) => void;
  onAdd: () => void;
}

function FilterBar(p: FilterBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-stone-200 dark:border-stone-700">
      <input
        placeholder="Search name or SKU…"
        value={p.search}
        onChange={(e) => p.onSearch(e.target.value)}
        className={`${selectClass} w-56`}
      />
      <select
        value={p.category}
        onChange={(e) => p.onCategory(e.target.value)}
        className={selectClass}
      >
        <option value="">All categories</option>
        {CATALOG_CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <label className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400">
        <input
          type="checkbox"
          checked={p.includeMerged}
          onChange={(e) => p.onIncludeMerged(e.target.checked)}
        />
        Show merged
      </label>
      <span className="ml-auto text-xs text-stone-400">{p.count} items</span>
      {p.canEdit && (
        <button
          onClick={p.onAdd}
          className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-moss-600 text-white hover:bg-moss-700 transition"
        >
          <Plus size={14} /> Add item
        </button>
      )}
    </div>
  );
}

function ItemRow({
  it,
  canEdit,
  onEdit,
  onMerge,
}: {
  it: CatalogItem;
  canEdit: boolean;
  onEdit: (i: CatalogItem) => void;
  onMerge: (i: CatalogItem) => void;
}) {
  return (
    <tr className="border-b border-stone-50 dark:border-stone-800/50 hover:bg-stone-50 dark:hover:bg-[#242d28]">
      <td className="px-4 py-2 text-stone-900 dark:text-stone-100">
        {it.name}
        {it.mergedIntoId && (
          <span className="ml-2 text-[10px] uppercase text-amber-600">merged</span>
        )}
        {!it.isActive && !it.mergedIntoId && (
          <span className="ml-2 text-[10px] uppercase text-stone-400">inactive</span>
        )}
      </td>
      <td className="px-4 py-2 text-stone-500">{it.category}</td>
      <td className="px-4 py-2 font-mono text-xs text-stone-500">{it.sku}</td>
      <td className="px-4 py-2 text-stone-500 truncate max-w-xs">{summarize(it.attributes)}</td>
      {canEdit && (
        <td className="px-4 py-2 text-right whitespace-nowrap">
          {!it.mergedIntoId && (
            <>
              <button
                onClick={() => onEdit(it)}
                className="text-xs text-moss-600 hover:underline mr-3"
              >
                Edit
              </button>
              <button
                onClick={() => onMerge(it)}
                className="text-xs text-amber-600 hover:underline"
              >
                Merge
              </button>
            </>
          )}
        </td>
      )}
    </tr>
  );
}

function ItemTable({
  items,
  canEdit,
  onEdit,
  onMerge,
}: {
  items: CatalogItem[];
  canEdit: boolean;
  onEdit: (i: CatalogItem) => void;
  onMerge: (i: CatalogItem) => void;
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-stone-400 border-b border-stone-100 dark:border-stone-700/50">
          <th className="px-4 py-2 font-medium">Name</th>
          <th className="px-4 py-2 font-medium">Category</th>
          <th className="px-4 py-2 font-medium">SKU</th>
          <th className="px-4 py-2 font-medium">Attributes</th>
          {canEdit && <th className="px-4 py-2" />}
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <ItemRow key={it.id} it={it} canEdit={canEdit} onEdit={onEdit} onMerge={onMerge} />
        ))}
      </tbody>
    </table>
  );
}

export default function ItemsTab({ canEdit }: { canEdit: boolean }) {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [includeMerged, setIncludeMerged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CatalogItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [merging, setMerging] = useState<CatalogItem | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchCatalogItems({ q: search || undefined, category: category || undefined, includeMerged })
      .then((r) => setItems(r.items))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [search, category, includeMerged]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const reloadAfter = (close: () => void) => () => {
    close();
    load();
  };

  return (
    <div className="bg-white dark:bg-[#1e2521] rounded-xl border border-stone-200 dark:border-stone-700">
      <FilterBar
        search={search}
        category={category}
        includeMerged={includeMerged}
        count={items.length}
        canEdit={canEdit}
        onSearch={setSearch}
        onCategory={setCategory}
        onIncludeMerged={setIncludeMerged}
        onAdd={() => setCreating(true)}
      />
      <div className="overflow-x-auto">
        {loading ? (
          <div className="py-8 text-center text-stone-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-stone-500 text-sm">No items found</div>
        ) : (
          <ItemTable items={items} canEdit={canEdit} onEdit={setEditing} onMerge={setMerging} />
        )}
      </div>

      {creating && (
        <ItemFormModal
          item={null}
          onClose={() => setCreating(false)}
          onSaved={reloadAfter(() => setCreating(false))}
        />
      )}
      {editing && (
        <ItemFormModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={reloadAfter(() => setEditing(null))}
        />
      )}
      {merging && (
        <MergeItemsModal
          source={merging}
          onClose={() => setMerging(null)}
          onMerged={reloadAfter(() => setMerging(null))}
        />
      )}
    </div>
  );
}
